import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ThemeProvider,
  Typography,
  DatePicker,
  Button,
  AppHeaderBackButton,
  useDatePickerValidator,
  Form,
} from '@ace-clublabs/shared-component-library';
import dayjs from 'dayjs';
import { useGlobalState } from '../shared/state';
import { Pages } from '../shared/pages';
import { UtagHelper } from '../utils/utagHelper';
import Header from '../components/Header';

export interface PaymentDateProps {
  minimumDueDate: string;
  cancelUrl: string;
  onNext: (e: any) => void;
}
export const PaymentDate: React.FC = () => {
  const [manifest] = useGlobalState('manifest');
  const [, setScheduledDate] = useGlobalState('scheduledDate');
  const [, setIsScheduled] = useGlobalState('isScheduled');
  const [sessionId] = useGlobalState('sessionId');

  const navigate = useNavigate();

  useEffect(() => {
    if (manifest?.tags) {
      const { subchannel1, subchannel2, channel } = manifest.tags || {};
      let page = subchannel2
        ? `${channel}:${subchannel1}:${subchannel2}:schedule payment`
        : `${channel}:${subchannel1}:schedule payment`;

      UtagHelper.tag({ ...manifest.tags, page, sessionid: sessionId });
    }
  }, []);

  const onNextClick = (date: string) => {
    setScheduledDate(date);

    const today = new Date();
    const splitDate = date.split('-');
    let selectedDate = new Date(NaN);
    if (splitDate.length === 3) {
      const [y, m, d] = splitDate.map(Number);
      selectedDate = new Date(y, m - 1, d);
    }

    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    setIsScheduled(selectedDate && selectedDate.getTime() > today.getTime());

    if (manifest?.renderPaymentMethods || !manifest?.paymentType) {
      navigate(Pages.PaymentMethods);
    } else {
      navigate(Pages.PaymentPage);
    }
  };

  return (
    <PaymentDateView
      minimumDueDate={manifest!.schedulePayment!.minimumDueDate}
      cancelUrl={manifest!.redirect!.cancelUrl!}
      onNext={onNextClick}
    />
  );
};

export const PaymentDateView: React.FC<PaymentDateProps> = (props) => {
  const [scheduledDate, setScheduledDate] = useGlobalState('scheduledDate');

  const { minimumDueDate, onNext } = props;
  const today = new Date();
  const minDueDate = new Date(`${minimumDueDate} 00:00`);

  const todayLocal = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate()
  ).padStart(2, '0')}`;

  const initialDate = scheduledDate || todayLocal;
  const currentValue = initialDate ? dayjs(initialDate) : null;
  const navigate = useNavigate();

  const formatDateForDisplay = (date: Date) => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear().toString();
    return `${month}/${day}/${year}`;
  };

  const helper_text = `You may select any day between ${formatDateForDisplay(today)} and ${formatDateForDisplay(
    minDueDate
  )}`;

  const validatorProps = useDatePickerValidator({
    minDate: todayLocal,
    maxDate: minimumDueDate,
  });

  const handleDateChange = (val: any) => {
    if (!val) return;
    let dateObj: Date;
    if (val instanceof Date) {
      dateObj = val;
    } else if (val?.$d instanceof Date) {
      dateObj = val.$d as Date;
    } else if (typeof val === 'string' || typeof val === 'number') {
      dateObj = new Date(val);
    } else if (typeof val?.toDate === 'function') {
      dateObj = val.toDate();
    } else {
      dateObj = new Date(val);
    }
    if (isNaN(dateObj.getTime())) return;
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');
    const formattedDate = `${yyyy}-${mm}-${dd}`;
    setScheduledDate(formattedDate);
  };

  return (
    <div id="payment-date-view" className="inner-container">
      <Header />
      <AppHeaderBackButton id="back-button" onClick={() => navigate(-1)} />
      <ThemeProvider>
        <Form>
          <Typography variant="h5" sx={{ fontWeight: '500', marginBottom: '14px' }} component="h1">
            Choose the date for your one-time payment
          </Typography>
          <DatePicker
            label="Payment date"
            defaultValue={currentValue}
            desktopCalendarHelperText={helper_text}
            calendarTitle="Select the payment date"
            mobileCalendarHelperText={helper_text}
            slotProps={{
              textField: {
                helperText: helper_text,
                placeholder: 'MM / DD / YYYY',
              },
            }}
            validator={validatorProps}
            onChange={handleDateChange}
          ></DatePicker>
          <Button
            id="next-button"
            variant="contained"
            fullWidth={true}
            label="Next"
            type="submit"
            onClick={() => {
              onNext(scheduledDate || initialDate);
            }}
            sx={{ marginBottom: '8px', marginTop: '16px' }}
          ></Button>
        </Form>
      </ThemeProvider>
    </div>
  );
};
